import type { ApiScope, WebhookEvent, SettingsPatch } from '@tes/domain';
import { ApiError, createApiClient, type RequestOptions } from '@tes/api-client';
import { clearToken, getToken } from './auth';
import type {
  ApiKeyRow,
  AskInput,
  AuditEventRow,
  BaseFileSwap,
  BulkDeletionPreview,
  Contract,
  CreateEnvelopeBody,
  CreateEnvelopeResult,
  DocumentSummary,
  EnvelopeDetail,
  EnvelopeStatus,
  EnvelopeSummary,
  ImportTemplatesResult,
  PageSize,
  PlatformSettings,
  SaveDesignPayload,
  Template,
  TemplateDeletionPreview,
  TemplateDeletionResult,
  TemplateExportDocument,
  TemplateSummary,
  UploadResult,
  VariableDef,
  VariableGroupDef,
  VariableSource,
  VariableType,
  WebhookDeliveryRow,
  WebhookEndpointRow,
} from './types';

export { ApiError };

/**
 * Everything a variable write may carry. Ask fields accept null to clear;
 * `source: 'auto'` makes the server clear them all anyway.
 */
export type VariableWrite = Partial<{
  key: string;
  label: string;
  type: VariableType;
  required: boolean;
  default: string;
  source: VariableSource;
  prompt: string | null;
  helpText: string | null;
  askInput: AskInput | null;
  options: string[] | null;
  askWhenKey: string | null;
  askWhenValue: string | null;
  groupKey: string | null;
}>;

// Same-origin via Caddy. The session JWT is read per request (it can be set
// after this module loads); a 401 on an authenticated call ends the session.
const client = createApiClient({
  getToken,
  onUnauthorized: () => {
    clearToken();
    if (!location.pathname.startsWith('/login')) location.href = '/login';
  },
});

const apiFetch = <T,>(path: string, init?: RequestInit, opts?: RequestOptions) =>
  client.request<T>(path, init, opts);

export const api = {
  login: (email: string, password: string) =>
    apiFetch<{ token: string; expiresIn: string }>(
      '/v1/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
      { auth: false },
    ),
  listTemplates: () => apiFetch<TemplateSummary[]>('/v1/templates'),
  getTemplate: (id: string) => apiFetch<Template>(`/v1/templates/${id}`),
  getPages: (id: string) =>
    apiFetch<{ templateId: string; pages: PageSize[] }>(`/v1/templates/${id}/pages`),
  createTemplate: (form: FormData) =>
    apiFetch<Template>('/v1/templates', { method: 'POST', body: form }),
  updateTemplate: (
    id: string,
    body: Partial<{
      name: string;
      description: string;
      requiresSignature: boolean;
      signingOrder: number | null;
    }>,
  ) => apiFetch<Template>(`/v1/templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  saveDesign: (id: string, body: SaveDesignPayload) =>
    apiFetch<Template>(`/v1/templates/${id}/design`, { method: 'PUT', body: JSON.stringify(body) }),
  /** dryRun reports the impact without writing; the apply repeats the upload. */
  replaceBaseFile: (
    id: string,
    file: File,
    opts: { dryRun?: boolean; dropOrphanedFields?: boolean } = {},
  ) => {
    const form = new FormData();
    form.append('file', file);
    if (opts.dryRun) form.append('dryRun', 'true');
    if (opts.dropOrphanedFields) form.append('dropOrphanedFields', 'true');
    return apiFetch<BaseFileSwap>(`/v1/templates/${id}/basefile`, { method: 'POST', body: form });
  },
  duplicateTemplate: (id: string, name?: string) =>
    apiFetch<Template>(`/v1/templates/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(name ? { name } : {}),
    }),
  exportTemplate: (id: string) =>
    apiFetch<TemplateExportDocument>(`/v1/templates/${id}/export`),
  exportAllTemplates: () => apiFetch<TemplateExportDocument>('/v1/templates/export'),
  /** Export just the ticked ones — same bulk document shape as "export all". */
  exportTemplates: (ids: string[]) =>
    apiFetch<TemplateExportDocument>(
      `/v1/templates/export?ids=${encodeURIComponent(ids.join(','))}`,
    ),
  /**
   * Sends JSON when the referenced base PDF is expected to be here, or multipart
   * (document + PDF) after a 409 says it isn't.
   */
  importTemplates: (doc: TemplateExportDocument, file?: File) => {
    if (!file) {
      return apiFetch<ImportTemplatesResult>('/v1/templates/import', {
        method: 'POST',
        body: JSON.stringify(doc),
      });
    }
    const form = new FormData();
    form.append('data', JSON.stringify(doc));
    form.append('file', file);
    return apiFetch<ImportTemplatesResult>('/v1/templates/import', {
      method: 'POST',
      body: form,
    });
  },
  publish: (id: string) =>
    apiFetch<{ version: number; versionId: string }>(`/v1/templates/${id}/publish`, {
      method: 'POST',
    }),
  archive: (id: string) => apiFetch<Template>(`/v1/templates/${id}/archive`, { method: 'POST' }),
  templateDeletionPreview: (id: string) =>
    apiFetch<TemplateDeletionPreview>(`/v1/templates/${id}/deletion-preview`),
  /** Set-aware preview for a bulk delete — see BulkDeletionPreview on why. */
  templatesDeletionPreview: (ids: string[]) =>
    apiFetch<BulkDeletionPreview>('/v1/templates/deletion-preview', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  /**
   * Admin-only. The server answers 409 when completed envelopes were signed
   * from this template; `force` is the explicit "destroy their history too".
   */
  deleteTemplate: (id: string, opts: { force?: boolean } = {}) =>
    apiFetch<TemplateDeletionResult>(`/v1/templates/${id}${opts.force ? '?force=true' : ''}`, {
      method: 'DELETE',
    }),
  getContract: (id: string) => apiFetch<Contract>(`/v1/templates/${id}/contract`),
  baseFileUrl: (id: string) => `/v1/templates/${id}/basefile`,

  // variable library
  listVariables: () => apiFetch<VariableDef[]>('/v1/variables'),
  createVariable: (body: VariableWrite & { key: string; label: string }) =>
    apiFetch<VariableDef>('/v1/variables', { method: 'POST', body: JSON.stringify(body) }),
  updateVariable: (id: string, body: VariableWrite) =>
    apiFetch<VariableDef & { renamedDraftFields: number }>(`/v1/variables/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteVariable: (id: string) => apiFetch<void>(`/v1/variables/${id}`, { method: 'DELETE' }),

  // choice groups (mutually-exclusive sets of checkbox variables)
  listVariableGroups: () => apiFetch<VariableGroupDef[]>('/v1/variable-groups'),
  createVariableGroup: (body: {
    key: string;
    prompt: string;
    helpText?: string | null;
    required?: boolean;
  }) => apiFetch<VariableGroupDef>('/v1/variable-groups', { method: 'POST', body: JSON.stringify(body) }),
  updateVariableGroup: (
    id: string,
    body: Partial<{ key: string; prompt: string; helpText: string | null; required: boolean }>,
  ) =>
    apiFetch<VariableGroupDef>(`/v1/variable-groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteVariableGroup: (id: string) =>
    apiFetch<void>(`/v1/variable-groups/${id}`, { method: 'DELETE' }),

  // documents
  listDocuments: () => apiFetch<DocumentSummary[]>('/v1/documents'),

  // uploads (ad-hoc PDFs for Quick send)
  createUpload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch<UploadResult>('/v1/uploads', { method: 'POST', body: form });
  },

  // envelopes
  listEnvelopes: () => apiFetch<EnvelopeSummary[]>('/v1/envelopes'),
  /** The key makes a retry of the same send return the original envelope, not a second one. */
  createEnvelope: (body: CreateEnvelopeBody, idempotencyKey: string) =>
    apiFetch<CreateEnvelopeResult>('/v1/envelopes', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(body),
    }),
  getEnvelope: (id: string) => apiFetch<EnvelopeDetail>(`/v1/envelopes/${id}`),
  envelopeEvents: (id: string) => apiFetch<AuditEventRow[]>(`/v1/envelopes/${id}/events`),
  envelopeAudit: (id: string) => apiFetch<{ auditUrl: string }>(`/v1/envelopes/${id}/audit`),
  /** Resend the outstanding signers' links — the same ones, unless `rotate` replaces them. */
  remindEnvelope: (id: string, rotate = false) =>
    apiFetch<{
      signers: {
        signerId: string;
        role: string;
        name: string;
        email: string;
        order: number | null;
        signingUrl: string;
        /** A different URL from before — the old one no longer opens. */
        newLink: boolean;
      }[];
      /** n8n endpoints subscribed to envelope.reminder that received the links. */
      webhookEndpoints: number;
    }>(`/v1/envelopes/${id}/remind`, { method: 'POST', body: JSON.stringify({ rotate }) }),
  /** Bearer-authenticated artifact routes — fetch these with `downloadWithAuth`, not a link. */
  documentDownloadPath: (id: string, type: 'generated' | 'signed' | 'original') =>
    `/v1/documents/${id}/download?type=${type}`,
  envelopeZipPath: (id: string) => `/v1/envelopes/${id}/download`,
  cancelEnvelope: (id: string) =>
    apiFetch<{ status: EnvelopeStatus }>(`/v1/envelopes/${id}/cancel`, { method: 'POST' }),

  // api keys
  listApiKeys: () => apiFetch<ApiKeyRow[]>('/v1/auth/api-keys'),
  createApiKey: (label: string, scopes: ApiScope[]) =>
    apiFetch<{ id: string; key: string; label: string; scopes: ApiScope[] }>('/v1/auth/api-keys', {
      method: 'POST',
      body: JSON.stringify({ label, scopes }),
    }),
  revokeApiKey: (id: string) =>
    apiFetch<void>(`/v1/auth/api-keys/${id}`, { method: 'DELETE' }),

  // webhooks
  listWebhooks: () => apiFetch<WebhookEndpointRow[]>('/v1/webhooks'),
  createWebhook: (url: string, events: WebhookEvent[]) =>
    apiFetch<{ id: string; secret: string; url: string; events: WebhookEvent[] }>('/v1/webhooks', {
      method: 'POST',
      body: JSON.stringify({ url, events }),
    }),
  updateWebhook: (id: string, body: { events?: WebhookEvent[]; url?: string; active?: boolean }) =>
    apiFetch<WebhookEndpointRow>(`/v1/webhooks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteWebhook: (id: string) => apiFetch<void>(`/v1/webhooks/${id}`, { method: 'DELETE' }),
  listDeliveries: () => apiFetch<WebhookDeliveryRow[]>('/v1/webhooks/deliveries'),

  // settings
  getSettings: () => apiFetch<PlatformSettings>('/v1/admin/settings'),
  patchSettings: (body: SettingsPatch) =>
    apiFetch<PlatformSettings>('/v1/admin/settings', { method: 'PATCH', body: JSON.stringify(body) }),

  // branding
  patchBranding: (body: {
    companyName?: string;
    primaryColor?: string;
    accentColor?: string;
    tutorialDesktopVideoUrl?: string;
    tutorialMobileVideoUrl?: string;
  }) =>
    apiFetch<PlatformSettings>('/v1/branding', { method: 'PATCH', body: JSON.stringify(body) }),
  uploadBrandLogo: (form: FormData) =>
    apiFetch<PlatformSettings>('/v1/branding/logo', { method: 'POST', body: form }),
  deleteBrandLogo: () =>
    apiFetch<PlatformSettings>('/v1/branding/logo', { method: 'DELETE' }),
  uploadFavicon: (form: FormData) =>
    apiFetch<PlatformSettings>('/v1/branding/favicon', { method: 'POST', body: form }),
  deleteFavicon: () =>
    apiFetch<PlatformSettings>('/v1/branding/favicon', { method: 'DELETE' }),
};

